import React, { useState, useEffect } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
  PointElement,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import annotationPlugin from "chartjs-plugin-annotation";
import TimeframeSelector from "./TimeframeSelector";
import TimeRangeSelector from "./TimeRangeSelector";
import ChartSkeleton from "./ChartSkeleton";
import TimeRangeSelectorSkeleton from "./TimeRangeSelectorSkeleton";
import TimeframeSelectorSkeleton from "./TimeframeSelectorSkeleton";
import PriceProgressBar from "./PriceProgressBar";
import OutlierToggle from "./OutlierToggle";
import OutlierToggleSkeleton from "./OutlierToggleSkeleton";
import PriceProgressBarSkeleton from "./PriceProgressBarSkeleton";
import moment from "moment";

ChartJS.register(annotationPlugin);
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  BarElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend
);

const HistoChart = ({ priceVolumeData, buyOrders, sellOrders }) => {
  const [selectedTimeframe, setSelectedTimeframe] = useState("hour");
  const [timeRange, setTimeRange] = useState("24h");
  const [chartData, setChartData] = useState({ datasets: [] });
  const [chartValues, setChartValues] = useState({});
  const [chartOptions, setChartOptions] = useState({});
  const [labelsVisible, setLabelsVisible] = useState([2, 3]);
  const [showOutliers, setShowOutliers] = useState(false);

  const handleOutlierToggle = (showOutliers) => {
    setShowOutliers(showOutliers);
  };

  const handleTimeRangeChange = (newTimeRange) => {
    setTimeRange(newTimeRange);
    let validTimeframe = selectedTimeframe;
    if (
      newTimeRange === "24h" &&
      (selectedTimeframe === "day" || selectedTimeframe === "week")
    ) {
      validTimeframe = "hour";
    } else if (newTimeRange === "7d" && selectedTimeframe === "week") {
      validTimeframe = "day";
    }

    if (validTimeframe !== selectedTimeframe) {
      setSelectedTimeframe(validTimeframe);
    }
  };

  const handleTimeframeChange = (newTimeframe) => {
    setSelectedTimeframe(newTimeframe);
  };

  // Function to group data by hour, day, or week and fill gaps
  const groupDataByTimeframe = (data, timeframe, timeRange) => {
    const now = moment();
    let filteredData;

    switch (timeRange) {
      case "24h":
        filteredData = data.filter((item) =>
          moment(item.date).isAfter(now.clone().subtract(24, "hours"))
        );
        break;
      case "7d":
        filteredData = data.filter((item) =>
          moment(item.date).isAfter(now.clone().subtract(7, "days"))
        );
        break;
      case "30d":
        filteredData = data.filter((item) =>
          moment(item.date).isAfter(now.clone().subtract(30, "days"))
        );
        break;
      case "all":
      default:
        filteredData = data;
        break;
    }

    const formatMap = {
      hour: "MMM-DD HH",
      day: "MMM-DD",
      week: "YYYY-ww",
    };

    const groupedData = filteredData.reduce((acc, currentItem) => {
      const date = moment(currentItem.date).format(formatMap[timeframe]);

      if (!acc[date]) {
        acc[date] = {
          open: parseFloat(currentItem.open),
          high: parseFloat(currentItem.high),
          low: parseFloat(currentItem.low),
          close: parseFloat(currentItem.close),
          volume: currentItem.volume,
        };
      } else {
        acc[date].high = Math.max(acc[date].high, parseFloat(currentItem.high));
        acc[date].low = Math.min(acc[date].low, parseFloat(currentItem.low));
        acc[date].close = parseFloat(currentItem.close);
        acc[date].volume += currentItem.volume;
      }

      return acc;
    }, {});

    const sortedDates = Object.keys(groupedData).sort((a, b) =>
      moment(a, formatMap[timeframe]).diff(moment(b, formatMap[timeframe]))
    );

    if (timeRange === "24h" || timeRange === "7d") {
      const startDate = moment(sortedDates[0], formatMap[timeframe]);
      const endDate = moment(
        sortedDates[sortedDates.length - 1],
        formatMap[timeframe]
      );
      const date = startDate.clone();
      let lastKnownValues = { ...groupedData[sortedDates[0]] };

      while (date.isBefore(endDate) || date.isSame(endDate)) {
        const formattedDate = date.format(formatMap[timeframe]);
        if (!groupedData[formattedDate]) {
          groupedData[formattedDate] = {
            open: lastKnownValues.close,
            high: lastKnownValues.close,
            low: lastKnownValues.close,
            close: lastKnownValues.close,
            volume: 0,
          };
        } else {
          lastKnownValues = groupedData[formattedDate];
        }
        date.add(1, timeframe);
      }
    }

    return Object.keys(groupedData)
      .sort((a, b) =>
        moment(a, formatMap[timeframe]).diff(moment(b, formatMap[timeframe]))
      )
      .map((date) => ({
        date,
        ...groupedData[date],
      }));
  };

  const updateChartData = (data) => {
    const lineData = data.map((item) => parseFloat(item.close));
    const barData = data.map((item) => item.volume);
    const labels = data.map((item) => item.date);

    const open = data.length > 0 ? data[0].open : null;
    const close = data.length > 0 ? data[data.length - 1].close : null;
    const min = Math.min(...data.map((item) => item.low));
    const max = Math.max(...data.map((item) => item.high));
    const volume = data.reduce((acc, item) => acc + item.volume, 0);
    const variation = ((close - open) / open) * 100;
    setChartValues({ open, close, min, max, volume, variation });

    setChartData({
      labels,
      datasets: [
        {
          type: "line",
          label: "Price",
          data: lineData,
          borderColor: "rgb(54, 162, 235)",
          backgroundColor: "rgb(54, 162, 235)",
          yAxisID: "y-price",
          pointRadius: 0,
          tension: 0.1,
          borderWidth: 2,
        },
        {
          type: "bar",
          label: "Volume",
          data: barData,
          borderColor: "RGB(209, 213, 219)",
          backgroundColor: "RGB(209, 213, 219)",
          yAxisID: "y-volume",
        },
        {
          type: "line",
          label: "Highest Buy Order",
          borderColor: "rgba(31, 211, 135, 1)",
          backgroundColor: "rgba(31, 211, 135, 1)",
          yAxisID: "y-price",
        },
        {
          type: "line",
          label: "Lowest Sell Order",
          borderColor: "rgba(190, 13, 27, 1)",
          backgroundColor: "rgba(190, 13, 27, 1)",
          yAxisID: "y-price",
        },
      ],
    });
  };

  useEffect(() => {
    if (!priceVolumeData || !buyOrders || !sellOrders) {
      return;
    }
    const groupedData = groupDataByTimeframe(
      priceVolumeData,
      selectedTimeframe,
      timeRange
    );
    const filteredData = showOutliers
      ? groupedData
      : filterTransactionsByStandardDeviation(groupedData);

    updateChartData(filteredData);

    const highestBuyOrderPrice = Math.max(
      ...buyOrders.map((order) => order.price)
    );
    const lowestClosePrice = Math.min(
      ...filteredData.map((item) => item.close)
    );
    const minPrice =
      Math.floor(
        Math.min(lowestClosePrice, highestBuyOrderPrice) * 0.95 * 1000
      ) / 1000;

    const lowestSellOrderPrice = Math.min(
      ...sellOrders.map((order) => order.price)
    );
    const highestClosePrice = Math.max(
      ...filteredData.map((item) => item.close)
    );
    const maxPrice =
      Math.ceil(
        Math.max(highestClosePrice, lowestSellOrderPrice) * 1.05 * 1000
      ) / 1000;

    setChartOptions({
      responsive: true,
      scales: {
        "y-price": {
          type: "linear",
          display: true,
          position: "left",
          beginAtZero: false,
          min: minPrice,
          max: maxPrice,
        },
        "y-volume": {
          ticks: {
            callback: function (value) {
              return value >= 1000 ? `${value / 1000}K` : value;
            },
          },
          type: "linear",
          display: true,
          position: "right",
          grid: {
            drawOnChartArea: false,
          },
        },
        x: {
          grid: {
            display: false,
          },
        },
      },
      plugins: {
        legend: {
          onClick: function (e, legendItem) {
            const index = legendItem.datasetIndex;
            const ci = this.chart;
            ci.data.datasets[index].hidden = !ci.data.datasets[index].hidden;
            ci.update();
            setLabelsVisible((currentLabels) => {
              const newLabelsVisible = currentLabels.includes(index)
                ? currentLabels.filter((label) => label !== index)
                : [...currentLabels, index];
              return newLabelsVisible;
            });
          },
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              let label = context.dataset.label || "";

              if (label) {
                label += ": ";
              }
              if (context.parsed.y !== null) {
                label += new Intl.NumberFormat("en-US", {
                  minimumFractionDigits: 5,
                  maximumFractionDigits: 5,
                }).format(context.parsed.y);
              }
              return label;
            },
          },
        },
        annotation: {
          annotations: {
            highestBuyOrder: {
              display: labelsVisible.includes(2),
              type: "line",
              yMin: highestBuyOrderPrice,
              yMax: highestBuyOrderPrice,
              borderColor: "rgba(31, 211, 135, 0.8)",
              borderWidth: 2,
              borderDash: [8, 8],
              label: {
                enabled: true,
                content: "Highest Buy Order",
                position: "start",
              },
            },
            lowestSellOrder: {
              display: labelsVisible.includes(3),
              type: "line",
              yMin: lowestSellOrderPrice,
              yMax: lowestSellOrderPrice,
              borderColor: "rgba(190, 13, 27, 0.8)",
              borderWidth: 2,
              borderDash: [8, 8],
              label: {
                enabled: true,
                content: "Lowest Sell Order",
                position: "start",
              },
            },
          },
        },
      },
    });
  }, [
    priceVolumeData,
    selectedTimeframe,
    timeRange,
    buyOrders,
    sellOrders,
    labelsVisible,
    showOutliers,
    chartValues.min,
    chartValues.max,
  ]);

  if (!chartValues.close) {
    return (
      <div className="flex flex-col mb-8">
        <div className="flex flex-wrap gap-4 mb-4">
          <TimeRangeSelectorSkeleton />
          <TimeframeSelectorSkeleton />
          <OutlierToggleSkeleton />
        </div>
        <PriceProgressBarSkeleton />
        <ChartSkeleton />
      </div>
    );
  }

  return (
    <div className="flex flex-col mb-8">
      <div className="flex flex-wrap gap-4 mb-4">
        <TimeRangeSelector onTimeRangeChange={handleTimeRangeChange} />
        <TimeframeSelector
          onTimeframeChange={handleTimeframeChange}
          selectedTimeframe={selectedTimeframe}
          timeRange={timeRange}
        />
        <OutlierToggle onToggleChange={handleOutlierToggle} />
      </div>
      <PriceProgressBar
        timeRange={timeRange}
        min={chartValues.min}
        max={chartValues.max}
        close={chartValues.close}
        variation={chartValues.variation}
        volume={chartValues.volume}
      />
      <Bar options={chartOptions} data={chartData} />
    </div>
  );
};

function filterTransactionsByStandardDeviation(
  transactions,
  numStandardDeviations = 2.5
) {
  const prices = transactions.map((transaction) =>
    parseFloat(transaction.close)
  );
  const mean = prices.reduce((acc, price) => acc + price, 0) / prices.length;
  const standardDeviation = Math.sqrt(
    prices.reduce((acc, price) => acc + (price - mean) ** 2, 0) / prices.length
  );
  const lowerBound = mean - numStandardDeviations * standardDeviation;
  const upperBound = mean + numStandardDeviations * standardDeviation;
  return transactions.filter(
    (transaction) =>
      parseFloat(transaction.close) >= lowerBound &&
      parseFloat(transaction.close) <= upperBound
  );
}

export default HistoChart;
